export const meta = {
  id: '44-passive-impersonal',
  order: 44,
  checkFocus: { voice: ['passive'] },   // what the end-of-lesson check tests first
  titleFi: 'Passiivi loppuun: miksi se ei ole suomen passiivi',
  titleEn: 'The passive in every tense, and impersonal constructions',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: [
    'āsikagon', 'sigligon', 'āpykagon', 'nādīnagon', 'ozletagon', 'humbagon', 'mazvestragon',
    'bēupyghagon', 'vēttetir', 'jokorigon', 'ūbremagon',
    'ours:tojanon',
    'ours:ojūnon', 'ours:vēttettys', 'ours:gierōñe', 'ours:jiknon',
    'ours:vēttōñe',
    'ours:jemēptys',
    'ours:mēriapos',
    'ours:pāleknāzma',
    'ours:ozūndella',
    'ours:kempēgrennon',
    'ours:dōrurnon',
    'ours:ozurnēbnon',
    'ours:jurnekio',
    'ours:ozbirvos',
    'ours:udriapos',
    'ours:ossȳngio',
    'ours:kempēgrentys',
    'ours:jurata',
    'ours:alibi',
    'ours:nāgeltinon',
    'ours:epēbnon',
    'ours:pressi',
    'ours:kyrkiapos',
    'ours:nābēnnon',
    'ours:federationi',
    'ours:qrillaodinon',
    'ours:ossēniros',
    'ours:udlikio',
    'ours:yrguragon',
    'ours:belmurvos',
    'ours:nābēmvos',
    'ours:geveri',
    'ours:shotguni',
    'ours:ozbiriros',
    'ours:autopsia',
    'ours:agenti',
    'ours:mafia',
    'ours:publisiteti',
    'ours:ekonomia',
    'ours:tanki',
  ],
  exampleIds: [
    'ex-421916375c25', 'got-s6e08-001', 'got-s5e03-006', 'got-s8e06-004', 'hotd-s1e04-009',
    'got-s5e03-007', 'hotd-s1e07-014', 'got-s4e10-010', 'hotd-s2e07-003', 'ex-211c2ced13b4',
    'ex-35b62de6a290', 'hotd-s1e02-002', 'ex-2a45f54b6225', 'ex-13537ed19fcd',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Conjugation',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verbal_Prefixes',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=Kostagon',
    'https://wiki.languageinvention.com/index.php?title=Humbagon',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['passive'],
  showLines: ['got-s6e08-001', 'got-s5e03-006', 'got-s8e06-004', 'hotd-s1e04-009', 'got-s5e03-007', 'hotd-s1e07-014', 'got-s4e10-010', 'hotd-s2e07-003', 'hotd-s1e02-002'],
  pronunciation: { focus: 'stress', words: ['āsikagon', 'sigligon', 'āpykagon', 'nādīnagon', 'ozletagon'], lines: ['got-s6e08-001', 'got-s5e03-006', 'got-s8e06-004'] },
  letter: {
    fi: 'Kirjoita uutinen Westerosista passiivissa.',
    en: 'Write a news item from Westeros in the passive.',
  },
  scene: 'got-s6e08-1',
}

# Passiivi loppuun: miksi se ei ole suomen passiivi

Duolingon *Passive voice* -osio opetti passiivin *-ks*-tunnuksen preesensissä, perfektissä ja
futuurissa, passiivin infinitiivin ja tekijän ilmaisemisen sanalla <Hv l="ondoso">ondoso</Hv>. Edellisillä
oppitunneilla olet nähnyt passiivia ohimennen muissakin aikamuodoissa (<Hv>brōziks</Hv>, <Hv>zālteks</Hv>).
Tällä oppitunnilla kootaan koko järjestelmä ja verrataan sitä suomeen. Molemmissa kielissä on
”passiivi”, mutta ne ovat kaksi aivan eri asiaa.

## Kolme muotoa joka aikamuodossa

Passiivissa on vain kolme eri persoonamuotoa: **yks. 1/3**, **2. persoona** (yksikkö ja monikko
samat) ja **mon. 1/3**. Muoto rakentuu aina samalla tavalla: aikamuodon vartalo ja teemavokaali + *ks*. Taulukon verbi on <Hv glossed>ipradagon</Hv> ’syödä’.

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Aikamuoto</th><th>yks. 1/3</th><th>2. p.</th><th>mon. 1/3</th><th>subjunktiivi yks. 1/3</th></tr></thead>
  <tbody>
    <tr><td>preesens</td><td><Hv glossed>ipradaks</Hv></td><td><Hv glossed>ipradāks</Hv></td><td><Hv glossed>ipradaksi</Hv></td><td><Hv glossed>ipradoks</Hv></td></tr>
    <tr><td>aoristi</td><td><Hv glossed>ipradiks</Hv></td><td><Hv glossed>ipradiaks</Hv></td><td><Hv glossed>ipradiksi</Hv></td><td><Hv glossed>ipraduks</Hv></td></tr>
    <tr><td>futuuri</td><td><Hv glossed>ipradilaks</Hv></td><td><Hv glossed>ipradilāks</Hv></td><td><Hv glossed>ipradiliks</Hv></td><td><Hv glossed>ipradiluks</Hv></td></tr>
    <tr><td>imperfekti</td><td><Hv glossed>ipradileks</Hv></td><td><Hv glossed>ipradilēks</Hv></td><td><Hv glossed>ipradiliks</Hv></td><td><Hv glossed>ipradiloks</Hv></td></tr>
    <tr><td>perfekti</td><td><Hv glossed>iprattaks</Hv></td><td><Hv glossed>iprattāks</Hv></td><td><Hv glossed>iprattaksi</Hv></td><td><Hv glossed>iprattoks</Hv></td></tr>
    <tr><td>pluskvamperfekti</td><td><Hv glossed>ipratteks</Hv></td><td><Hv glossed>iprattēks</Hv></td><td><Hv glossed>iprattiks</Hv></td><td><Hv glossed>iprattoks</Hv></td></tr>
    <tr><td>mennyt toistuva</td><td><Hv glossed>iprattiks</Hv></td><td><Hv glossed>iprattiaks</Hv></td><td><Hv glossed>iprattiksi</Hv></td><td><Hv glossed>iprattuks</Hv></td></tr>
  </tbody>
</table>
</div>

<Paradigm lemma="ipradagon" filter="indicative.passive" />

Huomaa:

- Futuurin ja imperfektin monikko on sama: <Hv glossed>ipradiliks</Hv> ’meidät/heidät syödään (tulevaisuudessa)’
  tai ’meitä/heitä syötiin (parhaillaan)’. Asiayhteys ratkaisee.
- Pluskvamperfektin monikko ja menneen toistuvan yksikkö ovat samat: <Hv>iprattiks</Hv>.

<Note>
**Mikä on varmaa?** Wikin taivutussivun mukaan vain preesensin passiivi tunnetaan kokonaan. Muiden
aikamuotojen taulukot on päätelty yksittäisistä tunnetuista muodoista (<Hv glossed>zālileks</Hv> ’minua
poltettiin’, <Hv glossed>zāltaks</Hv> ’minut poltettiin’, <Hv>zālteks</Hv>, <Hv>zālilaks</Hv>) sekä aoristin
<Hv>ōressiks</Hv> ja <Hv>ipradiks</Hv> (ja sarjasta <Hv>brōziks</Hv>). Sovelluksen taulukot noudattavat
wikin sanakirja-artikkelien taivutustaulukoita, ja ne ovat tästä samaa mieltä.
</Note>

## Passiivi sarjoissa

### Aoristi: Talisan kirje

Talisa kirjoittaa äidilleen. Tästä kirjeestä kannattaa ottaa mallia omiin kirjeisiinne:

<Example id="ex-421916375c25" />

<Interlinear hv="Nykēlo syt ūndon daor luo valzȳro ñōghossi ōr|essiks." gloss="itse-GEN puolesta nähdä-PRF.SBJV.1SG ei joka-GEN aviomies-GEN käsivarsi-INS.PL pitää-AOR.PASS.1SG" fi="Minua pitelevät sellaisen aviomiehen käsivarret, jota en koskaan odottanut saavani." />

Aoristin passiivi <Hv glossed>ōressiks</Hv> kuvaa tilaa, joka on nyt pysyvä: ’minua pidellään’.

### Perfekti: syntyä uudelleen

Punaiset papit Meereenissä puhuvat Daenerysista perfektin passiivilla:

<Line id="got-s5e03-006" />

<Line id="got-s6e08-001" />

<Interlinear hv="Hen perzȳ vȳs āmazverdagon āsitt|aks." gloss="-sta tuli-LOC maailma-ACC luoda.uudelleen-INF synnyttää.uudelleen-PRF.PASS.3SG" fi="Tulesta hänet synnytettiin uudelleen luomaan maailma uudeksi." />

Daenerys Tahrattomille viimeisellä tuotantokaudella – kaksi passiivin perfektiä 2. persoonassa:

<Line id="got-s8e06-004" />

<Interlinear hv="Jeme hen muñoti ñōghoti nādīnt|āks se hae buzdarȳti ūbrēd|āks." gloss="te-NOM -lta äiti-GEN.PL käsivarsi-DAT.PL irrottaa-PRF.PASS.2 ja kuin orja-LOC.PL kasvattaa-PRF.PASS.2" fi="Teidät kaikki revittiin äitienne sylistä ja kasvatettiin orjina." />

Ja kausatiivin passiivi – Rhaenyra äidistään:

<Line id="hotd-s1e04-009" />

<Hv glossed>Isētaks</Hv> ’hänet pakotettiin’ on verbin <Hv fi="pakottaa">isahagon</Hv> (vrt. *sahagon*, <LessonLink n={43}>oppitunti 43</LessonLink>) perfektin
passiivi, ja teko, johon hänet pakotettiin, on infinitiivissä: <Hv glossed>dārannī āpykagon</Hv> ’synnyttämään perillisiä’.

Lohikäärmeenvartija lyhyesti ja tylysti:

<Line id="hotd-s2e07-003" />

<Hv glossed>Vēttetir irūdaks</Hv> ’tuomio annettiin’ – kirjaimellisesti ’tuomio tuli annetuksi’.

### Futuuri ja preesens

<Line id="got-s5e03-007" />

<Hv glossed>Siglīliks</Hv> ’heidät synnytetään uudelleen’ on futuurin passiivin monikko.

Häälaulussa preesensin passiivin monikko kuvaa ajatonta tapahtumaa:

<Line id="hotd-s1e07-014" />

### Kielteinen passiivi

Kielteinen passiivi on subjunktiivissa, kuten aktiivikin:

<Line id="got-s4e10-010" />

<Hv glossed>Ūndetoks daor</Hv> ’ei ole nähty’ – perfektin passiivin subjunktiivi.

## Mikä voi olla passiivin subjekti

Passiivi tekee objektista subjektin. Jos verbissä on applikatiivi (<LessonLink n={45}>oppitunti 45</LessonLink>), passiivin
subjektiksi tulee **applikatiivin objekti** – Duolingo mainitsi tämän (*Ziry ilimataks* ’häntä
itkettiin’). Wikin esimerkkipari:

<Example id="ex-211c2ced13b4" />

<Example id="ex-35b62de6a290" />

<Hv glossed>Qurdon bēupȳdaks</Hv> ’pöydälle hypättiin’ – kirjaimellisesti ’pöytä hypättiin päälle’.

Kaikilla verbeillä ei ole passiivia. Wikin kielioppisivun mukaan esimerkiksi epäsäännöllisestä
<Hv>jagon</Hv>-verbistä johdetuilta verbeiltä <Hv glossed>henujagon</Hv> ’lähteä’ ja <Hv glossed>jokorigon</Hv> ’tutkia’
puuttuu passiivi muoto-opillisista syistä. Niille ei muodosteta passiivia edes
<Hv fi="on päättynyt">keliton issa</Hv> -tyyppisellä rakenteella.

## Valyrian passiivi vs. suomen passiivi

Tämä on oppitunnin tärkein kohta.

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th></th><th>suomi</th><th>korkeavalyria</th></tr></thead>
  <tbody>
    <tr><td>kuka on subjekti?</td><td>ei kukaan (tekijä on tuntematon ihminen)</td><td>kohde: nominatiivissa</td></tr>
    <tr><td>taipuuko verbi?</td><td>ei: <em>minut nähtiin, meidät nähtiin</em></td><td>kyllä: <Hv glossed>ūndaks</Hv> ’minut nähtiin’, <Hv glossed>ūndaksi</Hv> ’meidät nähtiin’</td></tr>
    <tr><td>voiko tekijän sanoa?</td><td>ei (kirjakielessä vältetään)</td><td>kyllä: <Hv glossed>valo ondoso</Hv> ’miehen toimesta’</td></tr>
    <tr><td>voiko tekijä olla eloton?</td><td>ei</td><td>kyllä, välinepassiivi</td></tr>
  </tbody>
</table>
</div>

Suomen passiivi on oikeastaan **persoonaton** muoto: ”täällä tanssitaan” ei kerro, kuka tanssii,
eikä siinä ole subjektia. Valyrian passiivi on **persoonallinen**: kohteesta tulee kieliopillinen
subjekti, ja verbi taipuu sen mukaan.

<Compare fi="minut näh|tiin" hv="ūnd|aks" label="passiivi: objekti pysyy objektina ~ objektista tulee subjekti" />

Siksi suomen passiivia ei voi mekaanisesti kääntää valyrian passiivilla. ”Täällä tanssitaan” ei
ole valyriaksi passiivi, koska tanssimisella ei ole kohdetta, joka voisi ”tulla tanssituksi”. Suomen ”sanotaan, että…” taas
käännetään valyriaksi **monikon 3. persoonan aktiivilla**, kuten Talisan kirjeessä:

<Example id="ex-13537ed19fcd" />

<Hv glossed>Vestris</Hv> = ’he sanovat’ = ’sanotaan’.

## Persoonattomat rakenteet

Valyriassa on muutama verbi, jota käytetään persoonattomasti yksikön 3. persoonassa ilman
subjektia. Duolingosta tunnet sääverbin <Hv>davābagon</Hv> sekä rakenteet <Hv fi="pitäisi">sytilības</Hv> ja
<Hv fi="täytyy">bēvilza</Hv>. <LessonLink n={39}>Oppitunnilla 39</LessonLink> tuli vastaan <Hv glossed>vestras</Hv> ’näyttää siltä’ + subjunktiivi. Tässä
kaksi lisää:

### *kostas* ’on mahdollista’

Sanakirjan mukaan <Hv glossed>kostagon</Hv> tarkoittaa myös ’olla mahdollista, että jokin (nominatiivi) tekee
jotakin (infinitiivi)’. Kuningas Viserys raivoaa:

<Line id="hotd-s1e02-002" />

<Interlinear hv="Skorkydoso zaldrīzo drōmon tōmēpsa urneroti rȳ laodikson kōtt|as?" gloss="miten lohikäärme-GEN muna-NOM viisikymmentä vartija-GEN.PL läpi varastaa-PASS.INF olla.mahdollista-PRF.3SG" fi="Miten on mahdollista, että lohikäärmeen muna varastettiin viidenkymmenen vartijan nenän edestä?" />

Huomaa passiivin infinitiivi: muna ”voi tulla varastetuksi” samaan tapaan kuin <LessonLink n={42}>oppitunnilla 42</LessonLink>.

### *humbas* ’on edelleen niin, että’

Sanakirjan mukaan <Hv glossed>humbagon</Hv> tarkoittaa persoonattomana ’jää (todeksi), että…’, ja sen kanssa
käytetään indikatiivia:

<Example id="ex-2a45f54b6225" />

<Interlinear hv="… yn ānogrosa gierī ozlet|aksi humb|ilza." gloss="… mutta veri-INS yhdessä sitoa-PRS.PASS.3PL jäädä-FUT.3SG" fi="…mutta he pysyvät verellä yhteen sidottuina." />

Kirjaimellisesti: ”jää (voimaan), että heidät on sidottu yhteen verellä”.

## Harjoituslauseita

<Practice hv="Rūniapos bardutaks." fi="Kirje on kirjoitettu." />

<Practice hv="Havon sīr iprattaks." fi="Leipä on jo syöty." />

<Practice hv="Zaldrīzesse rijiksi." fi="Lohikäärmeitä ylistetään (aina)." />

<Practice hv="Ziry dāria issa vestris." fi="Sanotaan, että hän on kuningatar." />

## Sanasto

<Vocab />

## Tarkistus

<Check />
